/**
 * Preset Edit Dialog
 *
 * Opens a preset for direct editing without applying it to any panel.
 * Shows all settings (size, engine params, enhancements) with
 * Advanced Applicability for params not universal to all models.
 */

import { useState, useEffect } from 'react';
import { Info, X, ChevronDown, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { EngineParamsForm } from './EngineParamsForm';
import { TagEditor } from './TagEditor';
import { ModelDefinitionListItem } from '@/lib/api';
import {
  ImagePreset,
  EnhancementKey,
  ProviderParams,
  TagDefinition,
  ENHANCEMENT_CATEGORIES,
  EMPTY_ENHANCEMENTS,
  getAllProviderParamDefinitions,
  FALLBACK_SIZE_OPTIONS,
} from '../types/image-generator.types';

export interface PresetEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preset: ImagePreset | null;
  tagDefinitions: TagDefinition[];
  availableModels?: ModelDefinitionListItem[];
  onSave: (presetId: string, updates: Partial<Omit<ImagePreset, 'id' | 'createdAt'>>) => void;
  onCreateTag: (name: string) => string;
}

export function PresetEditDialog({
  open,
  onOpenChange,
  preset,
  tagDefinitions,
  availableModels,
  onSave,
  onCreateTag,
}: PresetEditDialogProps) {
  // Local editing state — initialized from preset when dialog opens
  const [name, setName] = useState('');
  const [size, setSize] = useState('1:1');
  const [enhancements, setEnhancements] =
    useState<Record<EnhancementKey, string>>(EMPTY_ENHANCEMENTS);
  const [providerParams, setProviderParams] = useState<ProviderParams>({});
  const [tags, setTags] = useState<string[]>([]);

  const [basicOpen, setBasicOpen] = useState(true);
  const [enhancementsOpen, setEnhancementsOpen] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Reset local state when preset changes
  useEffect(() => {
    if (preset) {
      setName(preset.name);
      setSize(preset.size);
      setEnhancements({ ...EMPTY_ENHANCEMENTS, ...preset.enhancements });
      setProviderParams({ ...preset.providerParams });
      setTags([...preset.tags]);
    }
  }, [preset]);

  if (!preset) return null;

  // All engine params go in Advanced Applicability (no model-specific context)
  const advancedDefs = getAllProviderParamDefinitions(availableModels);

  const handleSave = () => {
    onSave(preset.id, {
      name: name.trim() || preset.name,
      size,
      enhancements,
      providerParams,
      tags,
    });
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] h-[90vh] flex flex-col p-0">
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-border">
          <DialogTitle className="text-lg">Edit Preset</DialogTitle>
        </DialogHeader>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="max-w-2xl mx-auto flex flex-col gap-6">
            {/* Preset Name */}
            <div className="space-y-2 pb-4 border-b border-border">
              <label className="text-sm font-medium">Preset Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter preset name..."
                className="text-sm"
              />
            </div>

            {/* Tags */}
            <div className="space-y-2 pb-4 border-b border-border">
              <label className="text-sm font-medium">Tags</label>
              <TagEditor
                assignedTagIds={tags}
                tagDefinitions={tagDefinitions}
                onAssignTag={(tagId) => setTags((prev) => [...prev, tagId])}
                onUnassignTag={(tagId) => setTags((prev) => prev.filter((t) => t !== tagId))}
                onCreateTag={onCreateTag}
              />
            </div>

            {/* Basic Settings */}
            <Collapsible open={basicOpen} onOpenChange={setBasicOpen}>
              <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 cursor-pointer select-none group">
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground transition-transform ${basicOpen ? '' : '-rotate-90'}`}
                />
                <h3 className="text-sm font-medium">Basic Settings</h3>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-4 pt-2 pb-4">
                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground">Aspect Ratio</label>
                    <Select value={size} onValueChange={setSize}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FALLBACK_SIZE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Prompt Enhancements */}
            <Collapsible open={enhancementsOpen} onOpenChange={setEnhancementsOpen}>
              <div className="pt-4 border-t border-border">
                <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 cursor-pointer select-none group">
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${enhancementsOpen ? '' : '-rotate-90'}`}
                  />
                  <h3 className="text-sm font-medium">Prompt Enhancements</h3>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent>
                <div className="space-y-4 pt-2 pb-4">
                  <p className="text-xs text-muted-foreground">
                    Add details to guide the AI's image generation. These will be appended to your
                    prompt.
                  </p>

                  {ENHANCEMENT_CATEGORIES.map((category) => (
                    <div key={category.key} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-muted-foreground">{category.label}</label>
                        <div className="relative group/info">
                          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-popover border border-border rounded-lg shadow-lg text-xs text-popover-foreground w-64 opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all z-[100]">
                            {category.description}
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Textarea
                          value={enhancements[category.key]}
                          onChange={(e) =>
                            setEnhancements((prev) => ({
                              ...prev,
                              [category.key]: e.target.value,
                            }))
                          }
                          placeholder={category.placeholder}
                          className="text-xs min-h-[60px] resize-none"
                          rows={2}
                        />

                        <div className="flex flex-col gap-1 flex-shrink-0">
                          {enhancements[category.key] && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                setEnhancements((prev) => ({
                                  ...prev,
                                  [category.key]: '',
                                }))
                              }
                              className="h-7 w-7"
                              title="Clear"
                              tabIndex={-1}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Advanced Applicability */}
            {advancedDefs.length > 0 && (
              <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                <div className="pt-4 border-t border-border">
                  <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 cursor-pointer select-none group">
                    <ChevronDown
                      className={`h-4 w-4 text-muted-foreground transition-transform ${advancedOpen ? '' : '-rotate-90'}`}
                    />
                    <h3 className="text-sm font-medium">Advanced Applicability</h3>
                  </CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                  <div className="space-y-4 pt-2 pb-4">
                    <p className="text-xs text-muted-foreground italic">
                      These settings are not applicable to all models. If you save these settings it
                      will apply them when applicable.
                    </p>
                    <EngineParamsForm
                      modelName=""
                      providerParams={providerParams}
                      onUpdate={setProviderParams}
                      definitions={advancedDefs}
                      allowUnset
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        </div>

        {/* Sticky footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-border bg-background">
          <div className="max-w-2xl mx-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={handleCancel} className="flex-1">
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!name.trim()} className="flex-1 gap-2">
              <Save className="h-4 w-4" />
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
